import { useRef, useState } from "react";
import { Radio, Select } from "antd";
import { Button, Icon, Modal, TextInput, Text, notification } from "components/common";
import {
  useShareContentMutation,
  useLazySearchPeopleQuery,
} from "features/community/apis";
import theme from "utils/themeVars";
import styles from "./index.module.less";

/**
 * Recommend a piece of content (script | quiz | study_session) to all followers
 * or specific people, with an optional note.
 */
const ShareButton = ({
  objectType,
  objectId,
  resource = {},
  size = "middle",
  type = "default",
  text = "Recommend",
  icon = "TbShare",
  buttonProps = {},
}) => {
  const [open, setOpen] = useState(false);
  const [audience, setAudience] = useState("all_followers");
  const [note, setNote] = useState("");
  const [selected, setSelected] = useState([]);
  const [options, setOptions] = useState([]);
  const searchTimer = useRef(null);
  const submittingRef = useRef(false);

  const [share, { isLoading }] = useShareContentMutation();
  const [searchPeople, { isFetching }] = useLazySearchPeopleQuery();

  const onSearch = (q) => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(async () => {
      if (!q) {
        setOptions([]);
        return;
      }
      const res = await searchPeople({ q, page: 1 });
      const data = res?.data?.data || [];
      setOptions(
        data.map((u) => ({
          label: `${u.name}${u.username ? ` (@${u.username})` : ""}`,
          value: u.user_code,
        }))
      );
    }, 350);
  };

  const reset = () => {
    setNote("");
    setSelected([]);
    setAudience("all_followers");
    setOptions([]);
  };

  const submit = async () => {
    if (submittingRef.current) return;
    if (!objectId) {
      notification.error({ message: "Nothing to share" });
      return;
    }
    if (audience === "targeted" && selected.length === 0) {
      notification.error({ message: "Select at least one person" });
      return;
    }
    submittingRef.current = true;
    const res = await share({
      object_type: objectType,
      object_id: objectId,
      audience,
      user_codes: audience === "targeted" ? selected : undefined,
      note: note.trim() || undefined,
    });
    submittingRef.current = false;
    if (res?.error) {
      notification.error({ message: res?.error?.message || "Share failed" });
      return;
    }
    notification.success({ message: "Recommended successfully" });
    setOpen(false);
    reset();
  };

  return (
    <>
      <Button
        size={size}
        type={type}
        icon={icon}
        iconProps={{ size: "16px" }}
        text={text}
        onClick={() => setOpen(true)}
        {...buttonProps}
      />

      <Modal
        visible={open}
        title="Recommend resource"
        okText="Recommend"
        loading={isLoading}
        onOk={submit}
        onCancel={() => {
          setOpen(false);
          reset();
        }}
        width={480}
      >
        <div className={styles.recommendForm}>
          <div className={styles.resourcePreview}>
            {resource.thumbnail || resource.coverImage ? <img src={resource.thumbnail || resource.coverImage} alt="" /> : <span><Icon icon={objectType === "quiz" ? "MdOutlineQuiz" : "AiOutlineBook"} size={20} /></span>}
            <div><Text className={styles.resourceType}>{objectType === "quiz" ? "Quiz" : objectType === "study_session" ? "Study session" : "Study script"}</Text><strong>{resource.title || resource.name || "Learning resource"}</strong><Text>{[resource.specialty, resource.description, resource.estimatedMinutes && `${resource.estimatedMinutes} min read`, resource.questionCount && `${resource.questionCount} questions`].filter(Boolean).join(" · ") || "Recommend this resource to support learning."}</Text></div>
          </div>
          <div>
            <Text style={{ fontWeight: 600 }}>Recommend to</Text>
            <div style={{ marginTop: 8 }}>
              <Radio.Group
                value={audience}
                onChange={(e) => setAudience(e.target.value)}
              >
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <Radio value="all_followers">My followers</Radio>
                  <Radio value="targeted">Specific people</Radio>
                </div>
              </Radio.Group>
            </div>
          </div>

          {audience === "targeted" && (
            <div>
              <Text style={{ fontWeight: 600 }}>Select people</Text>
              <Select
                mode="multiple"
                style={{ width: "100%", marginTop: 8 }}
                placeholder="Search by name or username"
                filterOption={false}
                onSearch={onSearch}
                loading={isFetching}
                value={selected}
                onChange={setSelected}
                options={options}
                notFoundContent={isFetching ? "Searching..." : "Type to search"}
              />
            </div>
          )}

          <div>
            <Text style={{ fontWeight: 600 }}>Why are you recommending this? (optional)</Text>
            <div style={{ marginTop: 8 }}>
              <TextInput
                placeholder="Great resource before tomorrow's exam."
                value={note}
                maxLength={500}
                handleChange={(e) => setNote(e?.target?.value)}
              />
            </div>
          </div>

          <Text className="gray-800" style={{ fontSize: 12, color: theme["gray600"] }}>
            Recommended content appears in your followers' community feed.
          </Text>
        </div>
      </Modal>
    </>
  );
};

export default ShareButton;
